import {UIEvent, useEffect, useMemo} from 'react';
import {throttle} from 'lodash';
import {Moon, PhoneCall} from 'lucide-react';
import {Call} from '../../store/features/calls/callsService';
import CallRow from './CallRow';
import OngoingCalls from './onGoingCalls';
import {lucide} from './callParts';

interface DndBanner {
    count: number;
    onShow: () => void;
    onHide: () => void;
}

interface CallListProps {
    calls: Call[];
    ongoingCalls: Call[];
    selectedCall?: Call;
    onSelect: (call: Call) => void;
    onReachEnd: () => void;
    isMobile: boolean;
    emptyMessage: string;
    dndBanner?: DndBanner;
}

const DndMissedBanner = ({count, onShow, onHide}: DndBanner) => (
    <div role="status" className="mx-2 mb-2 flex gap-3 rounded-card bg-surface-muted p-3">
        <Moon size={20} aria-hidden="true" className={`${lucide} mt-0.5 !text-ink-secondary`}/>
        <div className="flex flex-col gap-2">
            <div className="text-callout text-ink">You missed {count} calls while Do Not Disturb was on</div>
            <div className="flex gap-2">
                <button type="button" onClick={onShow} className="h-control-sm cursor-pointer rounded-control bg-surface px-3 text-footnote font-medium text-ink hover:bg-line">
                    Show me
                </button>
                <button type="button" onClick={onHide} className="h-control-sm cursor-pointer rounded-control px-3 text-footnote font-medium text-ink-muted hover:text-ink">
                    Hide
                </button>
            </div>
        </div>
    </div>
);

const CallList = ({calls, ongoingCalls, selectedCall, onSelect, onReachEnd, isMobile, emptyMessage, dndBanner}: CallListProps) => {
    const reachEnd = useMemo(() => throttle(onReachEnd, 2000), [onReachEnd]);
    useEffect(() => () => reachEnd.cancel(), [reachEnd]);

    const onScroll = (e: UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - (el.scrollTop + 30) <= el.clientHeight) reachEnd();
    };

    return (
        <div onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto px-2 pb-2" data-testid="call-list">
            {dndBanner && <DndMissedBanner {...dndBanner}/>}
            <OngoingCalls calls={ongoingCalls}/>
            {calls.map((call) => (
                <CallRow key={call.callId} call={call} isMobile={isMobile} handleCallClick={onSelect} selectedCall={selectedCall}/>
            ))}
            {calls.length === 0 && ongoingCalls.length === 0 && (
                <div className="flex flex-col items-center gap-3 px-6 py-16 text-center text-callout text-ink-muted">
                    <span className="flex size-12 items-center justify-center rounded-card bg-surface-muted text-ink-secondary">
                        <PhoneCall size={20} aria-hidden="true" className={lucide}/>
                    </span>
                    {emptyMessage}
                </div>
            )}
        </div>
    );
};

export default CallList;
